import { Box, Stack, Typography } from '@mui/material';
import type { GetTimetableResponse, GetTimetableStructureResponse, TimeSlot } from '@schooly/api';
import { DATE_FORMAT_WEEK_DAY_MID_FNS, WeekdayEnum } from '@schooly/api';
import { buildTestId, TIMETABLE } from '@schooly/constants/test-ids';
import { TypographyWithOverflowHint } from '@schooly/style';
import { FC, useCallback, useMemo } from 'react';

import { getWeekdayLabel } from '../../SchoolTimetableStructures/utils';
import { SlotAllocationsMap, SlotRoomMap, TimetableGroup } from '../types';
import { groupMatchesTimetableFilters, TimetableGridFilters } from '../utils';
import { resolveStructureRegisterSessions } from '../register-session-display';
import { ASSIGNABLE_PERIODS, TimetableSlotCard } from './TimetableSlotCard';

type TimetableGridProps = {
  structure: GetTimetableStructureResponse | GetTimetableResponse['structure'];
  slotAllocations: SlotAllocationsMap;
  slotRoomMap?: SlotRoomMap;
  groups: TimetableGroup[];
  filters: TimetableGridFilters;
  onSlotClick: (timeSlotId: string) => void;
  isEditable: boolean;
};

export const TIME_COLUMN_WIDTH = 120;
export const SIMPLE_SLOT_HEIGHT = 26;

export const TimetableGrid: FC<TimetableGridProps> = ({
  structure,
  slotAllocations,
  slotRoomMap,
  groups,
  filters,
  onSlotClick,
  isEditable,
}) => {
  const groupsMap = useMemo(() => {
    return new Map(groups.map((g) => [g.id, g]));
  }, [groups]);

  const getTimeSlot = useCallback(
    (periodId: string, weekday: WeekdayEnum): TimeSlot | undefined => {
      return structure.time_slots.find(
        (slot) => slot.period_id === periodId && slot.weekday === weekday,
      );
    },
    [structure.time_slots],
  );

  const structureAgeGroupIds = structure?.age_group_ids ?? [];

  const registerSessions = useMemo(
    () => resolveStructureRegisterSessions(structure.periods),
    [structure.periods],
  );

  const getGroupsForSlot = useCallback(
    (timeSlotId: string): TimetableGroup[] => {
      const allocations = slotAllocations[timeSlotId] ?? [];
      return allocations.map((a) => groupsMap.get(a.group_id)).filter(Boolean) as TimetableGroup[];
    },
    [slotAllocations, groupsMap],
  );

  const filtersActive = !!filters.age_group?.length || !!filters.subject?.length;
  const gridTemplateColumns = `${TIME_COLUMN_WIDTH}px repeat(${structure.weekdays.length}, minmax(0, 1fr))`;

  return (
    <Box sx={{ overflow: 'visible', minWidth: 0 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <Box />
        {structure.weekdays.map((weekday) => (
          <Box key={weekday} sx={{ textAlign: 'center', pb: 0.25 }}>
            <Typography variant="h4" color="common.grey2">
              {getWeekdayLabel(weekday, DATE_FORMAT_WEEK_DAY_MID_FNS)}
            </Typography>
          </Box>
        ))}

        {structure.periods.map((period) => (
          <Box key={period.id} sx={{ display: 'contents' }}>
            <Stack
              sx={{
                py: ASSIGNABLE_PERIODS.includes(period.type) ? 1 : 0.25,
                justifyContent: 'center',
                alignItems: 'center',
                height: ASSIGNABLE_PERIODS.includes(period.type) ? undefined : SIMPLE_SLOT_HEIGHT,
              }}
            >
              <TypographyWithOverflowHint color="common.grey2" noWrap>
                {period.start_time} – {period.end_time}
              </TypographyWithOverflowHint>
            </Stack>

            {structure.weekdays.map((weekday) => {
              const timeSlot = getTimeSlot(period.id, weekday);
              const slotGroups = timeSlot ? getGroupsForSlot(timeSlot.id) : [];

              const visibleGroups = filtersActive
                ? slotGroups.filter((group) => groupMatchesTimetableFilters(group, filters))
                : slotGroups;

              const noFilterMatches =
                filtersActive && slotGroups.length > 0 && !visibleGroups.length;

              const slotTestId = timeSlot
                ? buildTestId(
                    TIMETABLE.SLOT_PREFIX,
                    {
                      name: `${getWeekdayLabel(
                        weekday,
                        DATE_FORMAT_WEEK_DAY_MID_FNS,
                      ).toUpperCase()}-${period.start_time} – ${period.end_time}`,
                    },
                    'name',
                  )
                : undefined;

              return (
                <Box key={weekday} data-test-id={slotTestId} sx={{ minWidth: 0 }}>
                  {timeSlot && (
                    <TimetableSlotCard
                      timeSlot={timeSlot}
                      period={period}
                      registerSession={registerSessions.get(period.id) ?? null}
                      groups={visibleGroups}
                      structureAgeGroupIds={structureAgeGroupIds}
                      noFilterMatches={noFilterMatches}
                      groupRoomMap={slotRoomMap?.[timeSlot.id]}
                      onSlotClick={onSlotClick}
                      isEditable={isEditable}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
